/**
 * 格式化手机号，按照规则插入空格
 * @param phone 手机号
 * @param region Region，选填
 * @param interCode 国家码，选填
 * @returns 格式化后的手机号
 */
export function getFormatPhone(phone: string, region?: string, interCode?: string): string {
  if (typeof phone === 'string') {
    // do nothing
  } else if (typeof phone === 'number') {
    // 老逻辑有兼容 number，原因未知，让人费解。以防万一，暂时保留
    phone = (phone as number).toString();
  } else {
    // 其他类型不处理，包含 undefined 和 null
    return '';
  }

  // 数组元素的含义是每段有几个数字
  let rule = [4, 4, 4, 4];

  const phoneLength = phone.length;

  // PRD：https://km.sankuai.com/collabpage/2730963770
  // 巴西 +55 号码特殊处理拼接规则，其他情况走老逻辑
  if (region === 'BR' && interCode === '55') {
    if (phoneLength === 11) {
      rule = [2, 5, 4];
    } else if (phoneLength === 10) {
      rule = [2, 4, 4];
    }
  }

  const charList = phone.split('');
  let totalLength = 0;

  for (let i = 0; i < rule.length; i++) {
    const length = rule[i];
    const index = totalLength + length;
    if (index < charList.length) {
      charList.splice(index, 0, ' ');
      totalLength += length + 1;
    } else {
      break;
    }
  }

  const result = charList.join('');

  return result;
}

export function getScreenBottom(defaultBottom = 0) {
  let bottomAdaptation;
  if (Mach.env.isIOS && Mach.env.safeAreaInsets.bottom && Mach.env.safeAreaInsets.bottom > 0) {
    bottomAdaptation = Mach.env.safeAreaInsets.bottom;
  } else {
    bottomAdaptation = defaultBottom;
  }
  return bottomAdaptation;
}

const reg = /\r?\n/;
/**
 * 获取分割后的数组
 * @param {string} 原文本
 */
export function splitText(text: string) {
  if (!text) return [];
  return text.split(reg);
}

/**
 *  将json字符串转换为json对象
 * @param text 需要转换的字符串
 */
export function jsonParse(text: string): any {
  if (!text) return null;
  let res: any = null;
  try {
    res = JSON.parse(text);
  } catch (error) {
    res = text;
  }
  return res;
}

/**
 * 获取优惠类型用于 b_waimai_c_overseas_tr1j6t8d_mv 埋点的 discount_activity_type 0:满减 1:折扣 2:折扣菜 3:新客满减 4:新客折扣 9:新人折扣菜
 * @param activityTypeList 2:满减 3:满折 18:spu纬度折扣菜 9:使用红包 4:减配送费 5:一口价商品满减 21:免配送费
 * @param shopNewUser 1:门店新客 0:非门店新客
 */
export function getDiscountActivityType(activityTypeList: number[] = [], matchShopNewUser, orderInfo) {
  if (!Array.isArray(activityTypeList) || activityTypeList.length === 0) {
    return -999;
  }
  const isShopNewUser = matchShopNewUser === 1;
  let discountActivityTypes: number[] = [];
  // 满减
  const isReduce = activityTypeList.indexOf(2) !== -1;
  if (isReduce) {
    if (isShopNewUser) {
      discountActivityTypes.push(3);
    } else {
      discountActivityTypes.push(0);
    }
  }
  // 折扣
  const isDiscount = activityTypeList.indexOf(3) !== -1;
  if (isDiscount) {
    if (isShopNewUser) {
      discountActivityTypes.push(4);
    } else {
      discountActivityTypes.push(1);
    }
  }
  // 折扣菜
  const discountTypeList = getDiscountTypeList(orderInfo, activityTypeList);
  if (discountTypeList?.length > 0) {
    discountActivityTypes = discountActivityTypes.concat(discountTypeList);
  }

  // 919活动
  const isEvent919 = activityTypeList.indexOf(919) !== -1;
  if (isEvent919) {
    discountActivityTypes.push(6);
  }

  return discountActivityTypes.join(',') || -999;
}

// 获取折扣菜类型
function getDiscountTypeList(orderInfo, activityTypeList) {
  if (!activityTypeList?.some?.((type) => [18, 5].includes(type))) {
    return [];
  }
  return orderInfo?.skuList?.reduce((acc, sku) => {
    const targetUserType = sku?.targetUserType || 0;
    const tags = sku?.tags || [];
    if (targetUserType === 1 && (tags?.some?.((type) => [1, 3].includes(type))) && !acc.includes(9)) {
      acc.push(9);
    } else if (!acc.includes(2)) {
      acc.push(2);
    }
    return acc;
  }, []) || [];
}

/**
 * 存储催单时间、次数信息（订单Id维度）
 * @param orderViewId 订单ID
 * @param frequencyTime 频控时间（单位毫秒）
 * @param frequencyCount 频控次数
 * @returns
 */
export function storeRushInfoWithinFrequencyTime(orderViewId, frequencyTime = 300000, frequencyCount = 2) {
  if (!orderViewId) {
    return;
  }
  // 获取缓存存储信息
  const storageInfo = getOrderRushedTimeStorageInfo() || {};
  const currentTime = Date.now();
  // 计算频控时间前的时间戳
  const frequencyTimeAgo = currentTime - frequencyTime;
  // 记录更新未超过5分钟的信息，超过的舍弃
  const withinFiveMinutesInfo = {};
  Object.keys(storageInfo).forEach((key) => {
    const rushInfo = storageInfo[key];
    const time = parseFloat(rushInfo.time);
    const count = rushInfo.count;
    if (!Number.isNaN(time) && time > frequencyTimeAgo && count < frequencyCount) {
      withinFiveMinutesInfo[key] = rushInfo;
    }
  });
  const rushInfo = withinFiveMinutesInfo[orderViewId];
  // 更新催单频次
  const rushCount = (rushInfo?.count || 0) + 1;
  withinFiveMinutesInfo[orderViewId] = { time: currentTime, count: rushCount };
  const Storage = Mach.requireModule('Storage');
  Storage.setString({
    channel: 'mach',
    key: 'OrderRushedStorageInfo',
    value: JSON.stringify(withinFiveMinutesInfo)
  });
}

/**
 * 删除催单缓存信息
 * @param orderViewId
 * @returns
 */
export function deleteRushInfoByOrderViewId(orderViewId) {
  if (!orderViewId) {
    return;
  }
  // 获取缓存存储信息
  const storageInfo = getOrderRushedTimeStorageInfo() || {};
  if (storageInfo[orderViewId]) {
  // 移除该订单的催单缓存信息
    delete storageInfo[orderViewId];
    // 更新缓存
    const Storage = Mach.requireModule('Storage');
    Storage.setString({
      channel: 'mach',
      key: 'OrderRushedStorageInfo',
      value: JSON.stringify(storageInfo),
    });
  }
}

/**
 * 获取在频控时间内发生过催单
 * @param orderViewId 订单ID
 * @param frequencyTime 频控时间（单位毫秒），默认为5分钟
 * @returns
 */
export function hasRushedWithinLastFrequencyTime(orderViewId, frequencyTime = 300000, frequencyCount = 2) {
  if (!orderViewId) {
    return false;
  }
  const result = getOrderRushedTimeStorageInfo();
  const rushInfo = result?.[orderViewId];
  if (rushInfo) {
    const now = Date.now();
    const frequencyTimeAgo = now - frequencyTime;
    const { time = 0, count = 0 } = rushInfo || {};
    const hasRushed = !!((time > frequencyTimeAgo && count + 1 >= frequencyCount));
    return hasRushed;
  }
  return false;
}

/**
 * 是否有多次催单行为
 */
export function hasMoreRushWithinLastFrequencyTime(orderViewId, frequencyTime = 300000) {
  if (!orderViewId) {
    return false;
  }
  const result = getOrderRushedTimeStorageInfo();
  const rushInfo = result?.[orderViewId];
  if (rushInfo) {
    const now = Date.now();
    const frequencyTimeAgo = now - frequencyTime;
    const { time = 0, count = 0 } = rushInfo || {};
    const hasRushed = !!((time > frequencyTimeAgo && count > 0));
    return hasRushed;
  }
  return false;
}

/**
 * 获取订单催单时间存储信息
 * @returns
 */
function getOrderRushedTimeStorageInfo() {
  const Storage = Mach.requireModule('Storage');
  const result = Storage.getString({
    channel: 'mach',
    key: 'OrderRushedStorageInfo'
  });
  let storageInfo = {};
  if (result?.length > 0 && typeof result === 'string') {
    storageInfo = JSON.parse(result);
  }
  return storageInfo;
}
